/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Tabs } from 'react-bootstrap';
import { get, find, map } from 'lodash';
import ndjsonStream from 'can-ndjson-stream';

import moviesConfig from '../configs/movies-config';
import { TabbedFilters, FilterTypes } from '../../components/Filters';
import SampleSize from '../../components/SampleSize';
import Iframe from '../../components/Iframe';
import MovieInfo from './MovieInfo';
import tvtApi from '../../services/tvt-api';
import lookerApi from '../../services/looker-api';
import './styles/MovieReport.css';

import {
  renderAnticipationTab,
  renderAudienceTab,
  renderEngagementTab,
  renderAffinityTab
} from './Tabs';
import { serializeFilters } from '../../utils/filters';
import { contentReportTabConversion } from '../../utils/shared';
import {
  getCurrentTabFromPath,
  isReleased,
  getSampleSize,
} from '../../utils/movie';

import {
  benchmarkTooltipDeactivated,
  titleInitialState,
  titleReducer
} from '../../utils/benchmark';

const MovieReport = props => {
  const [benchmarkData, dispatch] = useReducer(titleReducer, titleInitialState);
  const [benchmark, setBenchmark] = useState(null);
  const [displayBenchmarkTooltip, setDisplayBenchmarkTooltip] = useState(false);
  const [currentTab, setCurrentTab] = useState(
    getCurrentTabFromPath(props.history, moviesConfig, props.movieId)
  );

  const [followers, setFollowers] = useState(false);

  const updatePath = newTab => {
    const paths = props.history.location.pathname.split('/');
    const { query } = props.history.location;
    const lastPath = paths[paths.length - 1];
    const tabName = contentReportTabConversion(newTab);

    if (lastPath !== props.movieId) {
      paths[paths.length - 1] = tabName;
    } else {
      paths.push(tabName);
    }

    props.history.push({
      pathname: paths.join('/'),
      search: query
    });
  };

  const handleTabChange = nextTab => {
    setCurrentTab(nextTab);
  };

  const fetchBenchmarkData = async (
    filterValues = {},
    uuid,
    topData = false
  ) => {
    dispatch({ type: 'RESET', topData });
    const { benchmarks } = props;

    const fv = filterValues;
    if (uuid && uuid !== 'similar-movies') {
      const bm = find(benchmarks, b => b.uuid === uuid);
      let urlBenchmarkFilters;
      if (bm) {
        setBenchmark(bm);
        urlBenchmarkFilters = JSON.parse(get(bm, 'filters', ''));
      } else {
        const seg = await tvtApi.getSegment(uuid);
        urlBenchmarkFilters = JSON.parse(get(seg, 'filters', ''));
        setBenchmark(seg);
      }
      urlBenchmarkFilters.forEach(f => {
        fv[f.id] = f;
      });
    } else if (!topData) {
      setBenchmark(null);
    }

    const hasCorrectMovie =
      get(benchmarkData, ['movieId'], null) === props.movieId;

    const body = {
      filters: serializeFilters(fv),
      page: currentTab,
      movieId: props.movieId,
      tier: hasCorrectMovie ? get(benchmarkData, ['tier', 'tier'], null) : null,
      topData
    };
    
    if (body.filters.length === 0) {
      body.filters.push("movie:");      
    }

    const apiRequestId = JSON.stringify(body);
    const dispatchObj = { type: 'INITIATE_REQUEST', topData };
    if (topData) {
      dispatchObj.apiRequestTop = apiRequestId;
    } else {
      dispatchObj.apiRequestBottom = apiRequestId;
    }
    dispatch(dispatchObj);
    try {
      const response = await lookerApi.createBenchmarkRequest(body);
      const reader = ndjsonStream(response.body).getReader();

      const read = r => {
        if (r.done) {
          dispatch({ type: 'LOADING_DONE', topData });
          return;
        }
        map(r.value, (v, k) => {
          dispatch({
            type: 'UPDATE',
            value: r.value,
            [topData ? 'apiRequestTop' : 'apiRequestBottom']: apiRequestId,
            movieId: props.movieId
          });
        });
        reader.read().then(read);
      };

      reader.read().then(result => {
        read(result);
      });
    } catch (err) {
      dispatch({ type: 'LOADING_DONE' });
      console.log(err);
    }
  };

  const updateFilters = () => {
    const {
      filtersActions: { getFiltersForPageAsync }
    } = props;

    getFiltersForPageAsync(currentTab, {
      movieId: props.movieId,
      syncWithUrl: true
    });
  };

  useEffect(() => {
    const query =
      benchmarkData.movieReportAnticipationTop ||
      benchmarkData.movieReportViewershipTop ||
      benchmarkData.movieReportEngagementTop;

    if (query) {
      setFollowers(query.followers || null);
    }
  }, [
    benchmarkData.movieReportAnticipationTop,
    benchmarkData.movieReportViewershipTop,
    benchmarkData.movieReportEngagementTop
  ]);

  useEffect(() => {
    if (props.movie) {
      document.title = `${props.movie.name} | Movie | CVM Insights`;
    } else {
      document.title = 'Loading | CVM Insights';
    }

    return () => {
      document.title = document.URL;
    };
  }, [props.movie]);

  useEffect(() => {
    const {
      filtersActions: { resetFilters },
      fetchMovie
    } = props;

    setCurrentTab(
      getCurrentTabFromPath(props.history, moviesConfig, props.movieId)
    );
    resetFilters();
    updateFilters();
    fetchMovie(props.movieId);
    fetchBenchmarkData({}, props.selectedBenchmark);
  }, [props.movieId]);

  useEffect(() => {
    updateFilters();
    updatePath(currentTab);

    if (!benchmarkData[`${currentTab}Top`] && currentTab  !== moviesConfig.MOVIE_AFFINITY_PAGE_ID) {
      fetchBenchmarkData({}, props.selectedBenchmark);
    }
  }, [currentTab]);

  useEffect(() => {
    const shouldDisplay = benchmarkTooltipDeactivated();
    if (shouldDisplay === 'false') {
      setDisplayBenchmarkTooltip(false);
    } else {
      setTimeout(() => setDisplayBenchmarkTooltip(true), 2000);
    }
  }, [localStorage]);

  const renderMovieInfo = () => {
    const isAudienceTab = currentTab === moviesConfig.MOVIE_VIEWERSHIP_PAGE_ID;
    const sampleSize = isAudienceTab
      ? getSampleSize(props.viewershipQueries.movieSampleSize, props.blocked)
      : 0;

    return (
      <header className="tvt-main__header">
        <Row className="tvt-add-flex">
          <Col md={10}>
            <MovieInfo movie={props.movie} count={followers} />
          </Col>
          <Col md={2} className="tvt-column--bottom-element">
            {sampleSize > 0 && <SampleSize population={sampleSize} pinRight />}
          </Col>
        </Row>
      </header>
    );
  };
  const isMovieReleased = isReleased(get(props.movie, 'status'));
  const showEmptyState = () => !props.movie;

  return showEmptyState() ? (
    <Row className="tvt-add-flex">
      <Col md={10}>
        <MovieInfo loading />
      </Col>
    </Row>
  ) : (
    <div>
      {props.blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}

      {!props.blocked && (
        <>
          {renderMovieInfo()}
          <Tabs
            activeKey={currentTab}
            className="tvt-show-report tvt-main__content-tabs"
            id="show-detail-tabs"
            onSelect={handleTabChange}
          >
            <TabbedFilters
              requiredSegmentValues={['movie']}
              hideFilters={['movie']}
              defaultState
            />
            {renderAnticipationTab(
              props,
              props.movie,
              currentTab,
              benchmarkData,
              fetchBenchmarkData,
              benchmark,
              displayBenchmarkTooltip,
              setDisplayBenchmarkTooltip
            )}
            {isMovieReleased &&
              renderAudienceTab(
                props,
                props.movie,
                currentTab,
                benchmarkData,
                fetchBenchmarkData,
                benchmark,
                displayBenchmarkTooltip,
                setDisplayBenchmarkTooltip
              )}
            {isMovieReleased &&
              renderEngagementTab(
                props,
                props.movie,
                currentTab,
                benchmarkData,
                fetchBenchmarkData,
                benchmark,
                displayBenchmarkTooltip,
                setDisplayBenchmarkTooltip
              )}
            {renderAffinityTab(
                props,
                currentTab,
                isMovieReleased
              )}
          </Tabs>
        </>
      )}
    </div>
  );
};

export default MovieReport;

MovieReport.propTypes = {
  blocked: PropTypes.bool.isRequired,
  movieId: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.string
    }).isRequired
  }).isRequired,
  fetchMovie: PropTypes.func.isRequired,

  queryActions: PropTypes.shape({
    runInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  viewershipQueries: PropTypes.shape({
    audienceRatings: PropTypes.shape({}),
    movieFavorites: PropTypes.shape({}),
    movieFollowers: PropTypes.shape({}),
    movieSampleSize: PropTypes.shape({})
  }).isRequired,

  engagementQueries: PropTypes.shape({
    movieFollowers: PropTypes.shape({})
  }).isRequired,

  anticipationQueries: PropTypes.shape({
    movieFollowers: PropTypes.shape({})
  }).isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired,

  match: PropTypes.shape({
    params: PropTypes.shape({
      movieId: PropTypes.string
    })
  }).isRequired,
  movie: PropTypes.shape({})
};

MovieReport.defaultProps = {
  movie: null
};
